import type { NetPriceData, PriceData, TotalPriceData, WithSurcharge } from "~/domains/pricing/types/PriceData";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import type { LineItemBuilder } from "~/utils/cartUpdating/LineItemUpdate";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { multiply, zeroNetPrice } from "~/utils/PriceUtils";
import { zeroTotalPrice } from "~/utils/PriceUtils";

export const lineItemHasSurcharge = (lineItem: LineItem) => (lineItem.netPrice?.surcharge ?? 0) > 0;
export const lineItemHasTariffSurcharge = (lineItem: LineItem) => !!lineItem.netPrice?.tariffSurcharge;

export const getVisibleNetPrice = (
  packEntry: LineItem,
  productOrSubscription: ProductData | ProductDescriptor | boolean,
): NetPriceData | undefined => {
  if (!packEntry.priceOverriden) {
    return packEntry.netPrice;
  }
  if (!packEntry.offerPrice) {
    return undefined;
  }
  return {
    ...(packEntry.netPrice ?? zeroNetPrice(productOrSubscription)),
    value: packEntry.offerPrice.value,
    baseValue: packEntry.offerPrice.value,
    surcharge: 0,
  };
};

function getTotalPrice(price: NetPriceData | null | undefined, quantity: number): TotalPriceData & WithSurcharge {
  if (!price || !price.value) {
    return zeroTotalPrice(false);
  }

  const { unit, baseValue, isSubscription, surcharge, currencyIso, tariffSurcharge } = price;

  let baseTotal = (baseValue * quantity) / unit;
  if (isSubscription) {
    baseTotal = baseTotal * 12;
  }

  return {
    currencyIso,
    isSubscription,
    surcharge,
    tariffSurcharge,
    value: baseTotal * (1 + surcharge),
    baseValue: baseTotal,
  };
}

export function getTotalPriceForLineItem(
  lineItem: LineItem,
  product: ProductData | ProductDescriptor,
): TotalPriceData & WithSurcharge;
export function getTotalPriceForLineItem(lineItem: LineItem, isSubscription: boolean): TotalPriceData & WithSurcharge;

export function getTotalPriceForLineItem(
  lineItem: LineItem,
  productOrSubscription: ProductData | ProductDescriptor | boolean,
): PriceData {
  const visiblePrice = getVisibleNetPrice(lineItem, productOrSubscription) ?? zeroNetPrice(productOrSubscription);
  return getTotalPrice(visiblePrice, lineItem.quantity);
}

export function getPricePerPiece(
  lineItem: LineItem | LineItemBuilder | NetPriceData | undefined,
): PriceData | undefined {
  if (!lineItem) {
    return undefined;
  }

  let netPrice: NetPriceData | undefined;
  if ("unit" in lineItem) {
    netPrice = lineItem;
  } else if ("get" in lineItem) {
    netPrice = lineItem.get("netPrice");
  } else {
    netPrice = lineItem.netPrice;
  }

  const unit = netPrice?.unit ?? 1;

  if (!netPrice || unit === 1) {
    return netPrice;
  }

  return multiply(netPrice, 1 / unit);
}
